import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { MessagesSquare, Lock, Plus, Crown, Loader2, MessageCircle, Award, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

export default function Forum() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [isVip, setIsVip] = useState(false);
  const [active, setActive] = useState(null); // null = all
  const [threads, setThreads] = useState([]);
  const [digest, setDigest] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "general", title: "", body: "" });
  const [saving, setSaving] = useState(false);

  const loadCats = () => api.get("/forum/categories").then(({ data }) => { setCats(data.categories); setIsVip(data.is_vip); }).catch(() => {});
  const loadDigest = () => api.get("/forum/digest").then(({ data }) => setDigest(data)).catch(() => {});
  const loadThreads = (cat) => {
    setLoading(true);
    api.get("/forum/threads", { params: cat ? { category: cat } : {} })
      .then(({ data }) => setThreads(data)).catch(() => setThreads([])).finally(() => setLoading(false));
  };

  useEffect(() => { loadCats(); loadDigest(); }, []);
  useEffect(() => { loadThreads(active); }, [active]);

  const selectCat = (c) => {
    if (c.locked) { toast.error("The VIP Lounge is for VIP members."); navigate("/billing"); return; }
    setActive(c.id === active ? null : c.id);
  };

  const submit = async () => {
    if (!user) { navigate("/login"); return; }
    if (!form.title.trim() || !form.body.trim()) return toast.error("Add a title and a message.");
    setSaving(true);
    try {
      const { data } = await api.post("/forum/threads", form);
      toast.success("Thread posted");
      setOpen(false);
      setForm({ category: "general", title: "", body: "" });
      loadCats();
      navigate(`/forum/${data.thread_id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not post");
      if (e.response?.status === 402) navigate("/billing");
    } finally {
      setSaving(false);
    }
  };

  const postableCats = cats.filter((c) => !c.locked);

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary"><MessagesSquare className="mr-2 inline h-3.5 w-3.5" /> Community Forum</p>
            <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tighter sm:text-4xl">Talk projects & pricing worldwide</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-thread-btn" onClick={(e) => { if (!user) { e.preventDefault(); navigate("/login"); } }}
                className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                <Plus className="mr-2 h-4 w-4" /> New Thread
              </Button>
            </DialogTrigger>
            <DialogContent className="border-border">
              <DialogHeader><DialogTitle className="font-heading">Start a thread</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="thread-category" className="mt-1.5 rounded-sm border-border bg-transparent"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(isVip ? cats : postableCats).map((c) => <SelectItem key={c.id} value={c.id}>{c.label}{c.vip_only ? " (VIP)" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Title</Label>
                  <Input data-testid="thread-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    className="mt-1.5 rounded-sm border-border bg-transparent" placeholder="e.g. Kitchen remodel costs in Canada?" />
                </div>
                <div>
                  <Label className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Message</Label>
                  <Textarea data-testid="thread-body" value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                    rows={5} className="mt-1.5 rounded-sm border-border bg-transparent" placeholder="Share the details…" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} disabled={saving} data-testid="thread-submit"
                  className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Post Thread
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {digest.length > 0 && (
          <div className="mt-8 border border-border border-l-2 border-l-primary bg-secondary/10 p-6" data-testid="forum-digest">
            <p className="flex items-center gap-2 font-mono2 text-xs uppercase tracking-widest text-primary"><Award className="h-4 w-4" /> Top answers</p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {digest.map((d, i) => (
                <Link key={i} to={`/forum/${d.thread_id}`} data-testid={`digest-item-${i}`}
                  className="group border border-border bg-background p-4 transition-colors hover:border-primary">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">{d.category_label}</span>
                    <span className="flex shrink-0 items-center gap-2 font-mono2 text-[10px] uppercase tracking-widest">
                      {d.is_best && <span className="inline-flex items-center gap-1 text-primary"><Award className="h-3 w-3" /> Best</span>}
                      <span className="inline-flex items-center gap-0.5 text-muted-foreground"><ChevronUp className="h-3 w-3" />{d.upvotes}</span>
                    </span>
                  </div>
                  <h3 className="mt-1 truncate font-heading text-sm font-semibold group-hover:text-primary">{d.thread_title}</h3>
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{d.snippet}</p>
                  <p className="mt-2 font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">
                    {d.author_name}{d.author_vip && <span className="text-primary"> · VIP</span>}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 grid gap-8 lg:grid-cols-4">
          {/* Categories */}
          <aside className="lg:col-span-1">
            <p className="mb-3 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Categories</p>
            <div className="space-y-2">
              <button onClick={() => setActive(null)} data-testid="cat-all"
                className={`flex w-full items-center justify-between border px-3 py-2 text-left font-mono2 text-xs uppercase tracking-widest transition-colors ${active === null ? "border-primary text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                All recent
              </button>
              {cats.map((c) => (
                <button key={c.id} onClick={() => selectCat(c)} data-testid={`cat-${c.id}`}
                  className={`flex w-full items-center justify-between border px-3 py-2 text-left font-mono2 text-xs uppercase tracking-widest transition-colors ${active === c.id ? "border-primary text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                  <span className="flex items-center gap-1.5">{c.vip_only && <Crown className="h-3.5 w-3.5 text-primary" />}{c.label}</span>
                  {c.locked ? <Lock className="h-3.5 w-3.5" /> : <span className="text-[10px]">{c.thread_count}</span>}
                </button>
              ))}
            </div>
          </aside>

          {/* Threads */}
          <main className="lg:col-span-3">
            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : threads.length === 0 ? (
              <div className="border border-border p-10 text-center">
                <MessageCircle className="mx-auto h-8 w-8 text-muted-foreground/50" />
                <p className="mt-3 font-heading text-lg text-muted-foreground">No threads yet — be the first to post.</p>
              </div>
            ) : (
              <div className="divide-y divide-border border border-border" data-testid="thread-list">
                {threads.map((t, i) => (
                  <motion.div key={t.thread_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                    <Link to={`/forum/${t.thread_id}`} data-testid={`thread-${t.thread_id}`}
                      className="flex items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-secondary/20">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="border border-border px-2 py-0.5 font-mono2 text-[10px] uppercase tracking-widest text-primary">{t.category_label}</span>
                          <h3 className="truncate font-heading font-semibold">{t.title}</h3>
                        </div>
                        <p className="mt-1 flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">
                          {t.author_name}
                          {t.author_vip && <span className="inline-flex items-center gap-1 text-primary"><Crown className="h-3 w-3" /> VIP</span>}
                          · {fmt(t.last_activity)}
                        </p>
                      </div>
                      <div className="shrink-0 text-right font-mono2 text-xs text-muted-foreground">
                        <MessageCircle className="ml-auto h-4 w-4" />
                        <span>{t.reply_count}</span>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
}
